import { LOCALE_ID, NgModule, provideZoneChangeDetection } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule, EVENT_MANAGER_PLUGINS } from '@angular/platform-browser';
import { RouteReuseStrategy } from '@angular/router';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { CustomRouteReuseStrategy } from './strategies/route-reuse.strategy';

import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { JwtModule, JwtModuleOptions } from '@auth0/angular-jwt';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader, provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { environment } from 'src/environments/environment';
import { BaseHttpService } from './services/http/base.http.service';
import { TooltipModule } from './shared/tooltip/tooltip.module';

import { CommonModule, registerLocaleData } from '@angular/common';
import localeCa from '@angular/common/locales/ca';
import localeEl from '@angular/common/locales/el';
import localeEn from '@angular/common/locales/en';
import localeEs from '@angular/common/locales/es';
import localeEu from '@angular/common/locales/eu';
import localeFr from '@angular/common/locales/fr';
import localeIt from '@angular/common/locales/it';
import localePt from '@angular/common/locales/pt';
import { GoogleMapsModule } from '@angular/google-maps';
import { Select2 } from 'ng-select2-component';
import { ClipboardModule } from 'ngx-clipboard';
import { LottieComponent, provideLottieOptions } from 'ngx-lottie';
import { httpInterceptProviders } from './interceptors/http-interceptors';
import { pages } from './pages';
import { components } from './components';
import { PointerClickPlugin } from './shared/plugins/pointer-click.plugin';
import { ScrollFixPlugin } from './shared/plugins/scroll-fix.plugin';
import { sharedComponents } from './shared';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { availabilitiesComponents } from './components/availabilities';
import { APP } from 'src/app/config/app';

registerLocaleData(localeEn, 'en-GB');
registerLocaleData(localeEs, 'es-ES');
registerLocaleData(localeCa, 'ca-ES');
registerLocaleData(localeEl, 'el-GR');
registerLocaleData(localeIt, 'it-IT');
registerLocaleData(localePt, 'pt-PT');
registerLocaleData(localeEu, 'eu-ES');
registerLocaleData(localeFr, 'fr-FR');

export function tokenGetter() {
  return document.cookie.split('; ').reduce((r, v) => {
    const parts = v.split('=');
    return parts[0] === 'token' ? decodeURIComponent(parts.slice(1).join('=')) : r;
  }, null as string | null) || localStorage.getItem(APP.LOCAL_STORAGE_TOKEN_KEY_NAME);
}

const JWT_Module_Options: JwtModuleOptions = {
  config: {
    tokenGetter,
    allowedDomains: [environment.apiUrl],
  },
};

@NgModule({
  declarations: [
    AppComponent,
    [...pages],
    [...components],
    [...sharedComponents],
    [...availabilitiesComponents]
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    TranslateModule.forRoot({
        defaultLanguage: 'es',
        loader: {
            provide: TranslateLoader,
            useClass: TranslateHttpLoader,
        },
    }),
    JwtModule.forRoot(JWT_Module_Options),
    TooltipModule,
    FormsModule,
    ReactiveFormsModule,
    GoogleMapsModule,
    Select2,
    LottieComponent,
    ClipboardModule,
    CommonModule,
    BrowserAnimationsModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatOptionModule,
    MatSelectModule,
    NgxSkeletonLoaderModule
],
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    ...provideTranslateHttpLoader({ prefix: './assets/i18n/', suffix: `.json?v=${Date.now()}` }),
    BaseHttpService,
    httpInterceptProviders,
    provideHttpClient(withInterceptorsFromDi()),
    { provide: LOCALE_ID, useValue: 'en-GB' },
    { provide: LOCALE_ID, useValue: 'es-ES' },
    { provide: LOCALE_ID, useValue: 'ca-ES' },
    { provide: LOCALE_ID, useValue: 'it-IT' },
    { provide: LOCALE_ID, useValue: 'el-GR' },
    { provide: LOCALE_ID, useValue: 'pt-PT' },
    { provide: LOCALE_ID, useValue: 'eu-ES' },
    { provide: LOCALE_ID, useValue: 'fr-FR' },
    provideLottieOptions({
      player: () => import('lottie-web'),
    }),
    {
      provide: EVENT_MANAGER_PLUGINS, useClass: PointerClickPlugin, multi: true
    },
    {
      provide: EVENT_MANAGER_PLUGINS, useClass: ScrollFixPlugin, multi: true
    },
    { provide: RouteReuseStrategy, useClass: CustomRouteReuseStrategy }
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}

